import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  VideoOff, Grid2x2, Grid3x3, Square, Search,
  Maximize2, Minimize2, Circle, RefreshCw, Camera as CameraIcon, X, Wifi, WifiOff,
  CloudDownload, Loader2, Users, UserPlus, UserMinus, ChevronDown, Check,
  LayoutGrid, List, Server, Radio, AlertTriangle, Volume2, VolumeX, Plus,
  Settings, MonitorPlay, Zap, PowerOff, Power,
  Building2, ChevronLeft, Home, Briefcase, Star, Mic, MicOff,
  Rocket,
} from 'lucide-react';
import { useCamerasStore } from '@/stores/cameras-store';
import type { AccountCameraOption } from '@/stores/cameras-store';
import { StatusBadge } from '@/components/StatusBadge';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CAMERA_STATUS_CONFIG, CAMERA_PROTOCOL_LABELS, CAMERA_SOURCE_CONFIG, CAMERA_DEVICE_TYPE_CONFIG } from '@asi/shared';
import type { Camera, CameraStatus, CameraSource, ClientCamera } from '@asi/shared';
import { cn } from '@/lib/utils';
import { supabase } from '@/services/supabase';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { getHlsStream, getSnapshot, getCameraAssignments, assignCameraToClient, unassignCameraFromClient, listClients, disableCamera, enableCamera, setCameraPublicDemo } from '@/services/cameras';
import { useGo2rtcPlayer, useGo2rtcTalkback } from '@/hooks/use-go2rtc';

const GO2RTC_BASE = (import.meta.env.VITE_GO2RTC_URL as string | undefined) || 'https://cameras.corporacionasi.com';

const STATUS_FILTERS: Array<CameraStatus | 'ALL'> = ['ALL', 'ONLINE', 'OFFLINE', 'ERROR', 'MAINTENANCE'];
const STATUS_FILTER_LABELS: Record<string, string> = { ALL: 'Todas', ...Object.fromEntries(Object.entries(CAMERA_STATUS_CONFIG).map(([k, v]) => [k, v.label])) };

const SOURCE_FILTERS: Array<CameraSource | 'ALL'> = ['ALL', 'GATEWAY', 'HCT'];
const SOURCE_FILTER_LABELS: Record<string, string> = {
  ALL: 'Todas las fuentes',
  GATEWAY: 'Gateway (ONVIF)',
  HCT: 'Hik-Connect (HCT)',
};

type Layout = '1x1' | '2x2' | '3x3' | '4x4';
const LAYOUT_ICONS: Record<Layout, typeof Grid2x2> = {
  '1x1': Square,
  '2x2': Grid2x2,
  '3x3': Grid3x3,
  '4x4': LayoutGrid,
};
const LAYOUT_COLS: Record<Layout, string> = {
  '1x1': 'grid-cols-1',
  '2x2': 'grid-cols-2',
  '3x3': 'grid-cols-3',
  '4x4': 'grid-cols-4',
};

/** Check if a camera is an NVR channel (format SERIAL:CHANNEL in hikvision_device_id). */
function isNvrChannel(camera: Camera): boolean {
  return camera.protocol === 'HIKVISION_HCT' && !!camera.hikvision_device_id?.includes(':');
}

/** Parse NVR serial and channel number from a camera's metadata. */
function parseNvrInfo(camera: Camera): { serial: string; channelNo: number } | null {
  if (!camera.hikvision_device_id?.includes(':')) return null;
  const [serial, ch] = camera.hikvision_device_id.split(':');
  return { serial, channelNo: Number(ch) };
}

/** Get snapshot params for any HCT camera (standalone or NVR channel). */
function getSnapshotParams(camera: Camera): { serial: string; channelNo: number } | null {
  if (camera.protocol !== 'HIKVISION_HCT' || !camera.hikvision_device_id) return null;
  const nvrInfo = parseNvrInfo(camera);
  if (nvrInfo) return nvrInfo;
  // Standalone camera: serial is the hikvision_device_id, channel 1
  return { serial: camera.hikvision_device_id, channelNo: 1 };
}

// EZVIZ CDN segments are 3.333s long (standard HLS, not LL-HLS).
// To minimize live latency we stay 1 segment behind the live edge instead of the default 3.
// Achievable minimum: ~3–4 s (1 segment + player decode time).
const HLS_CONFIG = {
  enableWorker: true,
  lowLatencyMode: true,
  liveSyncDurationCount: 1,       // stay 1 segment behind live edge (default: 3)
  liveMaxLatencyDurationCount: 3, // if drift > 3 segments, snap-sync to live edge
  maxBufferLength: 8,             // don't buffer more than 8 s ahead (default: 30)
  maxMaxBufferLength: 12,         // hard cap (default: 600)
} as const;


const HLS_AUTO_RETRY_LIMIT = 6;
const HLS_AUTO_RETRY_DELAY_MS = 8000;

/** Resolve the HLS URL for a camera — only NVR channels support cloud HLS. */
function useHlsUrl(camera: Camera): { url: string | null; error: string | null; retry: () => void } {
  const [dynamicUrl, setDynamicUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  // Tracks how many automatic warm-up retries have been done (separate from manual retries)
  const autoRetries = useRef(0);
  // Cached proxy URL — reuse the same EZVIZ session across retries instead of creating a new
  // session (and resetting the warm-up clock) on every attempt.
  const cachedProxyUrl = useRef<string | null>(null);

  // Clear cache when camera changes
  useEffect(() => { cachedProxyUrl.current = null; }, [camera.hikvision_device_id]);

  // Re-fetch when tab becomes visible after being hidden >10s
  useEffect(() => {
    let hiddenAt = 0;
    function handle() {
      if (document.visibilityState === 'hidden') { hiddenAt = Date.now(); return; }
      if (hiddenAt > 0 && Date.now() - hiddenAt > 10_000) {
        autoRetries.current = 0;
        cachedProxyUrl.current = null;
        setAttempt(a => a + 1);
      }
    }
    document.addEventListener('visibilitychange', handle);
    return () => document.removeEventListener('visibilitychange', handle);
  }, []);

  useEffect(() => {
    if (camera.protocol !== 'HIKVISION_HCT') return;
    if (camera.status !== 'ONLINE' || !camera.hikvision_device_id) return;

    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    setError(null);
    setDynamicUrl(null);

    (async () => {
      try {
        // Reuse cached URL — avoids creating a new EZVIZ stream session (and restarting
        // the warm-up clock) on every retry attempt.
        if (!cachedProxyUrl.current) {
          const { url } = await getHlsStream(camera.hikvision_device_id!);
          if (cancelled) return;
          cachedProxyUrl.current = url;
        }

        // Pre-flight: fetch the proxied m3u8 to check if the camera is actually streaming
        const res = await fetch(cachedProxyUrl.current);
        if (cancelled) return;

        if (!res.ok) {
          const body = await res.json().catch(() => ({})) as Record<string, string>;
          const code = body.code;
          if (code === 'STREAM_ENCRYPTED') throw new Error('STREAM_ENCRYPTED');
          if (code === 'CAMERA_NOT_STREAMING') throw new Error('CAMERA_NOT_STREAMING');
          // For unexpected errors clear the cache so we get a fresh session next time
          cachedProxyUrl.current = null;
          throw new Error(body.error ?? 'Stream no disponible');
        }

        // Valid manifest — use it and reset auto-retry counter
        autoRetries.current = 0;
        setDynamicUrl(cachedProxyUrl.current);
      } catch (err) {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : 'Error al obtener stream';

        // CAMERA_NOT_STREAMING is transient — EZVIZ needs time to warm up the stream.
        // Schedule an auto-retry; the cached URL will be reused so the same EZVIZ session
        // keeps accumulating warm-up time instead of starting over.
        if (msg === 'CAMERA_NOT_STREAMING' && autoRetries.current < HLS_AUTO_RETRY_LIMIT) {
          autoRetries.current++;
          retryTimer = setTimeout(() => {
            if (!cancelled) setAttempt(a => a + 1);
          }, HLS_AUTO_RETRY_DELAY_MS);
          // Don't set error — snapshot polling continues in background (see useSnapshot below)
          return;
        }

        setDynamicUrl(null);
        setError(msg);
      }
    })();

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [camera.protocol, camera.status, camera.hikvision_device_id, attempt]);

  const retry = useCallback(() => {
    autoRetries.current = 0;
    cachedProxyUrl.current = null; // Force a fresh EZVIZ session on manual retry
    setDynamicUrl(null);
    setError(null);
    setAttempt(a => a + 1);
  }, []);

  if (camera.protocol === 'HIKVISION_HCT') return { url: dynamicUrl, error, retry };
  // Non-HCT cameras use a static HLS URL configured in DB
  return { url: camera.hls_url ?? null, error: null, retry: () => {} };
}

/** Poll snapshots for any HCT camera (NVR channels and standalone WiFi cameras). */
function useSnapshotUrl(camera: Camera, intervalMs = 5000): { url: string | null; loading: boolean; error: string | null; retry: () => void } {
  const [snapshotUrl, setSnapshotUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (camera.protocol !== 'HIKVISION_HCT' || camera.status !== 'ONLINE') return;

    const params = getSnapshotParams(camera);
    if (!params) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;

    const fetchSnapshot = async () => {
      try {
        const { url } = await getSnapshot(params.serial, params.channelNo);
        if (cancelled) return;
        setSnapshotUrl(url);
        setLoading(false);
        setError(null);
      } catch (err) {
        if (cancelled) return;
        setLoading(false);
        setError(err instanceof Error ? err.message : 'Error al obtener imagen');
      }
      if (!cancelled) {
        timer = setTimeout(fetchSnapshot, intervalMs);
      }
    };

    setLoading(true);
    fetchSnapshot();

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [camera.protocol, camera.status, camera.hikvision_device_id, intervalMs, attempt]);

  const retry = useCallback(() => {
    setSnapshotUrl(null);
    setError(null);
    setLoading(true);
    setAttempt(a => a + 1);
  }, []);

  return { url: snapshotUrl, loading, error, retry };
}

// ─── Stats Cards Component ──────────────────────────────────

function CameraStatsCards({
  statusCounts,
  sourceCounts,
  nvrCount,
}: {
  statusCounts: Record<string, number>;
  sourceCounts: Record<string, number>;
  nvrCount: number;
}) {
  const total = Object.values(statusCounts).reduce((a, b) => a + b, 0);
  const online = statusCounts.ONLINE ?? 0;
  const offline = statusCounts.OFFLINE ?? 0;
  const errored = statusCounts.ERROR ?? 0;

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
      <Card>
        <CardContent className="pt-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Total Cámaras</p>
              <p className="text-2xl font-bold">{total}</p>
            </div>
            <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/20">
              <CameraIcon className="w-5 h-5 text-blue-600" />
            </div>
          </div>
          <div className="flex gap-2 mt-2 text-xs flex-wrap">
            {sourceCounts.HCT > 0 && (
              <span className="text-orange-600">{sourceCounts.HCT} HCT</span>
            )}
            {sourceCounts.HPP > 0 && (
              <span className="text-purple-600">{sourceCounts.HPP} HPP</span>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">En Línea</p>
              <p className="text-2xl font-bold text-green-600">{online}</p>
            </div>
            <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/20">
              <Radio className="w-5 h-5 text-green-600" />
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            {total > 0 ? Math.round((online / total) * 100) : 0}% disponibilidad
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Desconectadas</p>
              <p className="text-2xl font-bold text-gray-500">{offline}</p>
            </div>
            <div className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800">
              <WifiOff className="w-5 h-5 text-gray-500" />
            </div>
          </div>
          {errored > 0 && (
            <p className="text-xs text-red-500 mt-2 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              {errored} con error
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">NVRs</p>
              <p className="text-2xl font-bold">{nvrCount}</p>
            </div>
            <div className="p-2 rounded-lg bg-indigo-100 dark:bg-indigo-900/20">
              <Server className="w-5 h-5 text-indigo-600" />
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-2">Con canales registrados</p>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Account Type Config ────────────────────────────────────

const ACCOUNT_TYPE_CONFIG: Record<
  'residential' | 'commercial' | 'enterprise',
  { label: string; icon: React.ReactNode; color: string; iconColor: string }
> = {
  residential: {
    label: 'Residencial',
    icon: <Home className="w-4 h-4" />,
    color: 'bg-blue-500/10',
    iconColor: 'text-blue-600 dark:text-blue-400',
  },
  commercial: {
    label: 'Comercio',
    icon: <Briefcase className="w-4 h-4" />,
    color: 'bg-amber-500/10',
    iconColor: 'text-amber-600 dark:text-amber-400',
  },
  enterprise: {
    label: 'Empresa',
    icon: <Star className="w-4 h-4" />,
    color: 'bg-purple-500/10',
    iconColor: 'text-purple-600 dark:text-purple-400',
  },
};

// ─── Account Picker ─────────────────────────────────────────

function AccountPicker({
  accounts,
  loading,
  onSelect,
}: {
  accounts: import('@/stores/cameras-store').AccountCameraOption[];
  loading: boolean;
  onSelect: (id: string | null) => void;
}) {
  const [search, setSearch] = useState('');

  const filtered = accounts.filter((a) =>
    a.name.toLowerCase().includes(search.toLowerCase()) ||
    a.account_number.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="p-6 pb-4 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
              <Building2 className="w-5 h-5 text-primary" />
              En Vivo — Seleccionar Cuenta
            </h2>
            <p className="text-sm text-muted-foreground mt-0.5">
              {accounts.length} cuentas activas
            </p>
          </div>
          <button
            onClick={() => onSelect('ALL')}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-secondary text-secondary-foreground hover:bg-secondary/80 transition-colors"
          >
            <CameraIcon className="w-4 h-4" />
            Ver todas las cámaras
          </button>
        </div>
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Buscar cuenta..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {/* Grid */}
      <div className="flex-1 overflow-auto p-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <Building2 className="w-12 h-12 text-muted-foreground/30 mb-3" />
            <p className="text-muted-foreground text-sm">No se encontraron cuentas</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {filtered.map((account) => {
              const typeCfg = ACCOUNT_TYPE_CONFIG[account.type];
              return (
                <button
                  key={account.id}
                  onClick={() => onSelect(account.id)}
                  className="group flex flex-col p-4 rounded-xl border border-border bg-card hover:border-primary/50 hover:bg-primary/5 transition-all text-left"
                >
                  {/* Type badge */}
                  <div className={cn('self-start flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium mb-3', typeCfg.color, typeCfg.iconColor)}>
                    {typeCfg.icon}
                    {typeCfg.label}
                  </div>
                  {/* Name */}
                  <p className="text-sm font-semibold text-foreground leading-tight line-clamp-2 mb-1 group-hover:text-primary transition-colors">
                    {account.name}
                  </p>
                  {/* Account number */}
                  <p className="text-xs text-muted-foreground mb-3">{account.account_number}</p>
                  {/* Camera count */}
                  <div className={cn(
                    'mt-auto flex items-center gap-1.5 text-xs font-medium',
                    account.camera_count > 0 ? 'text-green-600 dark:text-green-400' : 'text-muted-foreground'
                  )}>
                    <CameraIcon className="w-3.5 h-3.5" />
                    {account.camera_count > 0
                      ? `${account.camera_count} cámara${account.camera_count !== 1 ? 's' : ''}`
                      : 'Sin cámaras'}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Page ──────────────────────────────────────────────

export function CamerasPage() {
  const {
    loading, syncing, selectedCamera, filterStatus, filterSource, groupByNvr, search,
    fetchCameras, syncFromHikvision, selectCamera, setFilterStatus, setFilterSource,
    setGroupByNvr, setSearch, subscribeToCameras, filteredCameras, groupedCameras,
    statusCounts, sourceCounts, nvrCount,
    accountsForCameras, accountsLoading, selectedAccountId, fetchAccountsForCameras, setSelectedAccount,
  } = useCamerasStore();
  const navigate = useNavigate();

  const [layout, setLayout] = useState<Layout>('2x2');
  const [fullscreenCamera, setFullscreenCamera] = useState<Camera | null>(null);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const hlsInstances = useRef<Map<string, import('hls.js').default>>(new Map());

  // Snap all active HLS players simultaneously to their own live edge position.
  const syncLiveEdge = useCallback(() => {
    hlsInstances.current.forEach((hls) => {
      const target = hls.liveSyncPosition;
      if (target != null && hls.media) {
        hls.media.currentTime = target;
      }
    });
  }, []);

  // Load accounts on mount
  useEffect(() => {
    fetchAccountsForCameras();
  }, [fetchAccountsForCameras]);

  // Subscribe to realtime changes when an account is selected
  useEffect(() => {
    if (!selectedAccountId) return;
    const unsub = subscribeToCameras();
    return unsub;
  }, [selectedAccountId, subscribeToCameras]);

  const cameras = filteredCameras();
  const groups = groupedCameras();
  const counts = statusCounts();
  const sources = sourceCounts();
  const nvrs = nvrCount();
  const gridClass = LAYOUT_COLS[layout];

  const selectedAccount = accountsForCameras.find((a) => a.id === selectedAccountId);

  // Show account picker if no account is selected
  if (selectedAccountId === null) {
    return (
      <AccountPicker
        accounts={accountsForCameras}
        loading={accountsLoading}
        onSelect={setSelectedAccount}
      />
    );
  }

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Main Area */}
      <div className={cn('flex-1 flex flex-col min-w-0 overflow-hidden', selectedCamera && !fullscreenCamera && 'max-w-[calc(100%-320px)]')}>
        {/* Header */}
        <div className="p-6 pb-4 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* Back to accounts */}
              <button
                onClick={() => setSelectedAccount(null)}
                className="p-1.5 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                title="Volver a cuentas"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div>
                <h2 className="text-xl font-bold text-foreground leading-tight">
                  {selectedAccountId === 'ALL' ? 'Todas las Cámaras' : selectedAccount?.name ?? 'Cuenta'}
                </h2>
                {selectedAccountId !== 'ALL' && selectedAccount && (
                  <p className="text-xs text-muted-foreground">
                    {selectedAccount.account_number} · {ACCOUNT_TYPE_CONFIG[selectedAccount.type].label}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">

              {/* NVR Grouping toggle */}
              <Button
                variant={groupByNvr ? 'default' : 'outline'}
                size="sm"
                onClick={() => setGroupByNvr(!groupByNvr)}
                className="gap-2"
                title={groupByNvr ? 'Vista plana' : 'Agrupar por NVR'}
              >
                <Server className="w-4 h-4" />
                <span className="hidden sm:inline">NVR</span>
              </Button>

              {/* Sync from Hikvision */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => syncFromHikvision()}
                disabled={syncing}
                className="gap-2"
                title="Sincronizar cámaras desde Hikvision"
              >
                {syncing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <CloudDownload className="w-4 h-4" />
                )}
                <span className="hidden sm:inline">{syncing ? 'Sincronizando...' : 'Sincronizar'}</span>
              </Button>

              {/* Live edge sync */}
              <Button
                variant="outline"
                size="sm"
                onClick={syncLiveEdge}
                className="gap-2"
                title="Sincronizar borde en vivo de todas las cámaras"
              >
                <Radio className="w-4 h-4" />
                <span className="hidden sm:inline">En vivo</span>
              </Button>

              {/* Separator */}
              <div className="w-px h-6 bg-border" />

              {/* Layout toggle */}
              {(Object.entries(LAYOUT_ICONS) as [Layout, typeof Grid2x2][]).map(([l, Icon]) => (
                <button
                  key={l}
                  onClick={() => setLayout(l)}
                  className={cn(
                    'p-2 rounded-lg transition-colors',
                    layout === l
                      ? 'bg-secondary text-secondary-foreground'
                      : 'text-muted-foreground hover:bg-muted'
                  )}
                  title={l}
                >
                  <Icon className="w-4 h-4" />
                </button>
              ))}
            </div>
          </div>

          {/* Stats Cards */}
          <CameraStatsCards
            statusCounts={counts}
            sourceCounts={sources}
            nvrCount={nvrs}
          />

          {/* Filters bar */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Buscar cámara..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 w-56"
              />
            </div>

            {/* Source filter */}
            <select
              value={filterSource}
              onChange={(e) => setFilterSource(e.target.value as CameraSource | 'ALL')}
              className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm text-foreground"
            >
              {SOURCE_FILTERS.map((s) => (
                <option key={s} value={s}>
                  {SOURCE_FILTER_LABELS[s]} {s !== 'ALL' && `(${sources[s] ?? 0})`}
                </option>
              ))}
            </select>

            {/* Status filter pills */}
            <div className="flex gap-1.5">
              {STATUS_FILTERS.map((s) => {
                const count = s === 'ALL'
                  ? Object.values(counts).reduce((a, b) => a + b, 0)
                  : (counts[s] ?? 0);
                return (
                  <button
                    key={s}
                    onClick={() => setFilterStatus(s)}
                    className={cn(
                      'px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-1.5',
                      filterStatus === s
                        ? 'bg-secondary text-secondary-foreground'
                        : 'text-muted-foreground hover:bg-muted'
                    )}
                  >
                    {s !== 'ALL' && (
                      <span
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: CAMERA_STATUS_CONFIG[s]?.color }}
                      />
                    )}
                    {STATUS_FILTER_LABELS[s]}
                    <span className="opacity-60">({count})</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Camera Grid - Flat or Grouped by NVR */}
        <div className="flex-1 overflow-auto p-6 pt-0">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="w-8 h-8 border-2 border-secondary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : cameras.length === 0 ? (
            <EmptyState
              hasFilters={filterStatus !== 'ALL' || filterSource !== 'ALL' || search.length > 0}
              onSync={() => syncFromHikvision()}
              syncing={syncing}
              accountId={selectedAccountId !== 'ALL' ? selectedAccountId : null}
              onAssign={selectedAccountId !== 'ALL' ? () => navigate(`/accounts/${selectedAccountId}`) : undefined}
            />
          ) : groupByNvr ? (
            /* Grouped view */
            <div className="space-y-6">
              {Array.from(groups.entries()).map(([groupKey, cams]) => (
                <div key={groupKey}>
                  <div className="flex items-center gap-2 mb-3">
                    {groupKey === 'standalone' ? (
                      <>
                        <CameraIcon className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm font-medium text-muted-foreground">
                          Cámaras independientes ({cams.length})
                        </span>
                      </>
                    ) : (
                      <>
                        <Server className="w-4 h-4 text-indigo-500" />
                        <span className="text-sm font-medium text-foreground">
                          NVR {groupKey}
                        </span>
                        <Badge variant="secondary" className="text-xs">
                          {cams.length} canales
                        </Badge>
                      </>
                    )}
                  </div>
                  <div className={cn('grid gap-3 auto-rows-fr', gridClass)}>
                    {cams.map((camera) => (
                      <CameraCard
                        key={camera.id}
                        camera={camera}
                        isSelected={selectedCamera?.id === camera.id}
                        onClick={() => selectCamera(camera)}
                        onFullscreen={() => setFullscreenCamera(camera)}
                        compact={layout === '3x3' || layout === '4x4'}
                        onHlsReady={(hls) => {
                          if (hls) hlsInstances.current.set(camera.id, hls);
                          else hlsInstances.current.delete(camera.id);
                        }}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            /* Flat view */
            <div className={cn('grid gap-3 auto-rows-fr', gridClass)}>
              {cameras.map((camera) => (
                <CameraCard
                  key={camera.id}
                  camera={camera}
                  isSelected={selectedCamera?.id === camera.id}
                  onClick={() => selectCamera(camera)}
                  onFullscreen={() => setFullscreenCamera(camera)}
                  compact={layout === '3x3' || layout === '4x4'}
                  onHlsReady={(hls) => {
                    if (hls) hlsInstances.current.set(camera.id, hls);
                    else hlsInstances.current.delete(camera.id);
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Detail Panel */}
      {selectedCamera && !fullscreenCamera && (
        <CameraDetailPanel
          camera={selectedCamera}
          onClose={() => selectCamera(null)}
          onFullscreen={() => setFullscreenCamera(selectedCamera)}
        />
      )}

      {/* Fullscreen overlay */}
      {fullscreenCamera && (
        <FullscreenView
          camera={fullscreenCamera}
          onClose={() => setFullscreenCamera(null)}
        />
      )}

      {/* Assign devices dialog */}
      {showAssignDialog && (
        <AssignDevicesDialog
          open={showAssignDialog}
          onOpenChange={setShowAssignDialog}
          clientId={selectedAccountId ?? ''}
          clientName={selectedAccount?.name ?? 'Cuenta'}
          assignedCameraIds={new Set(cameras.map((c) => c.id))}
          onDone={() => { fetchCameras(); fetchAccountsForCameras(); }}
        />
      )}
    </div>
  );
}

// ─── Assign Devices Dialog ──────────────────────────────────

interface AlarmPanelRow {
  id: string;
  name: string;
  serial_number: string;
  model: string | null;
  status: string;
  site_id: string | null;
  site_name: string | null;
}

type DeviceTab = 'cameras' | 'panels';

function AssignDevicesDialog({
  open,
  onOpenChange,
  clientId,
  clientName,
  assignedCameraIds,
  onDone,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: string;
  clientName: string;
  assignedCameraIds: Set<string>;
  onDone: () => void;
}) {
  const [tab, setTab] = useState<DeviceTab>('cameras');
  const [allCameras, setAllCameras] = useState<Camera[]>([]);
  const [allPanels, setAllPanels] = useState<AlarmPanelRow[]>([]);
  const [assignedSiteIds, setAssignedSiteIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedCameras, setSelectedCameras] = useState<Set<string>>(new Set());
  const [selectedSites, setSelectedSites] = useState<Set<string>>(new Set());
  const [expandedNvrs, setExpandedNvrs] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);

  // Load all data when dialog opens
  useEffect(() => {
    if (!open) return;
    setSelectedCameras(new Set());
    setSelectedSites(new Set());
    setSearch('');
    setLoading(true);

    Promise.all([
      // Cameras
      supabase
        .from('cameras')
        .select('id, name, status, hikvision_device_id, site_id, protocol, device_type, nvr_serial, channel_no, device_manufacturer, device_model, go2rtc_stream_id, source')
        .order('nvr_serial', { nullsFirst: false })
        .order('channel_no')
        .order('name'),
      // Alarm panels with site info
      supabase
        .from('alarm_panels')
        .select('id, name, serial_number, model, status, site_id, alarm_sites(name)')
        .order('name'),
      // Already assigned sites for this client
      supabase
        .from('client_sites')
        .select('site_id')
        .eq('client_id', clientId),
    ]).then(([camRes, panelRes, siteRes]) => {
      setAllCameras((camRes.data as Camera[] | null) ?? []);
      setAllPanels(
        ((panelRes.data ?? []) as Array<Record<string, unknown>>).map((p) => ({
          id: p.id as string,
          name: p.name as string,
          serial_number: p.serial_number as string,
          model: p.model as string | null,
          status: p.status as string,
          site_id: p.site_id as string | null,
          site_name: (p.alarm_sites as { name: string } | null)?.name ?? null,
        })),
      );
      setAssignedSiteIds(
        new Set(((siteRes.data ?? []) as Array<{ site_id: string }>).map((s) => s.site_id)),
      );
      setLoading(false);
    });
  }, [open, clientId]);

  // ─── Camera grouping ──────────────────────────────────────

  const availableCameras = allCameras.filter((c) => !assignedCameraIds.has(c.id));

  // Group by NVR serial
  const nvrGroups = new Map<string, Camera[]>();
  const standaloneCameras: Camera[] = [];
  for (const cam of availableCameras) {
    if (cam.device_type === 'nvr_channel' && cam.nvr_serial) {
      const group = nvrGroups.get(cam.nvr_serial) ?? [];
      group.push(cam);
      nvrGroups.set(cam.nvr_serial, group);
    } else {
      standaloneCameras.push(cam);
    }
  }

  // Filter cameras by search
  const matchesSearch = (cam: Camera) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return cam.name.toLowerCase().includes(q)
      || cam.hikvision_device_id?.toLowerCase().includes(q)
      || cam.nvr_serial?.toLowerCase().includes(q);
  };

  // ─── Panel grouping ──────────────────────────────────────

  // Get unique site_ids from panels, filter out already assigned
  const availablePanels = allPanels.filter((p) => p.site_id && !assignedSiteIds.has(p.site_id));
  const panelsBySite = new Map<string, { siteName: string; panels: AlarmPanelRow[] }>();
  for (const panel of availablePanels) {
    if (!panel.site_id) continue;
    const group = panelsBySite.get(panel.site_id) ?? { siteName: panel.site_name ?? 'Sin sitio', panels: [] };
    group.panels.push(panel);
    panelsBySite.set(panel.site_id, group);
  }

  const matchesPanelSearch = (panel: AlarmPanelRow) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return panel.name.toLowerCase().includes(q)
      || panel.serial_number.toLowerCase().includes(q)
      || panel.site_name?.toLowerCase().includes(q);
  };

  // ─── Toggle handlers ─────────────────────────────────────

  const toggleCamera = (id: string) => {
    setSelectedCameras((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleNvr = (serial: string) => {
    const cameras = nvrGroups.get(serial) ?? [];
    const cameraIds = cameras.filter(matchesSearch).map((c) => c.id);
    const allSelected = cameraIds.every((id) => selectedCameras.has(id));
    setSelectedCameras((prev) => {
      const next = new Set(prev);
      for (const id of cameraIds) {
        if (allSelected) next.delete(id); else next.add(id);
      }
      return next;
    });
  };

  const toggleSite = (siteId: string) => {
    setSelectedSites((prev) => {
      const next = new Set(prev);
      if (next.has(siteId)) next.delete(siteId); else next.add(siteId);
      return next;
    });
  };

  const toggleExpandNvr = (serial: string) => {
    setExpandedNvrs((prev) => {
      const next = new Set(prev);
      if (next.has(serial)) next.delete(serial); else next.add(serial);
      return next;
    });
  };

  // ─── Save ─────────────────────────────────────────────────

  const totalSelected = selectedCameras.size + selectedSites.size;

  const handleSave = async () => {
    if (totalSelected === 0) return;
    setSaving(true);
    try {
      const promises: Promise<unknown>[] = [];

      // Assign cameras
      if (selectedCameras.size > 0) {
        promises.push(
          ...Array.from(selectedCameras).map((cameraId) =>
            assignCameraToClient(cameraId, clientId),
          ),
        );
      }

      // Assign sites (panels)
      if (selectedSites.size > 0) {
        for (const siteId of selectedSites) {
          promises.push(
            supabase.from('client_sites').insert({ client_id: clientId, site_id: siteId }) as unknown as Promise<unknown>,
          );
        }
      }

      await Promise.all(promises);
      onDone();
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  // ─── Render ───────────────────────────────────────────────

  const PANEL_STATUS_COLORS: Record<string, string> = {
    ONLINE: '#22C55E',
    OFFLINE: '#6B7280',
    ALARM: '#EF4444',
    FAULT: '#F59E0B',
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Asignar dispositivos a {clientName}</DialogTitle>
          <DialogDescription>
            Selecciona cámaras (por NVR o individual) y paneles de alarma para este cliente.
          </DialogDescription>
        </DialogHeader>

        {/* Tabs */}
        <div className="flex gap-1 bg-muted rounded-lg p-1">
          <button
            onClick={() => setTab('cameras')}
            className={cn(
              'flex-1 flex items-center justify-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
              tab === 'cameras' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <CameraIcon className="w-4 h-4" />
            Cámaras
            {selectedCameras.size > 0 && (
              <span className="bg-primary text-primary-foreground text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                {selectedCameras.size}
              </span>
            )}
          </button>
          <button
            onClick={() => setTab('panels')}
            className={cn(
              'flex-1 flex items-center justify-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
              tab === 'panels' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <Radio className="w-4 h-4" />
            Paneles de Alarma
            {selectedSites.size > 0 && (
              <span className="bg-primary text-primary-foreground text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                {selectedSites.size}
              </span>
            )}
          </button>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder={tab === 'cameras' ? 'Buscar por nombre, serial o NVR...' : 'Buscar por nombre, serial o sitio...'}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto min-h-0 max-h-[450px] space-y-1">
            {/* ─── Cameras tab ─── */}
            {tab === 'cameras' && (
              <>
                {availableCameras.length === 0 ? (
                  <div className="text-center py-10 text-muted-foreground text-sm">
                    Todas las cámaras ya están asignadas.
                  </div>
                ) : (
                  <>
                    {/* NVR groups */}
                    {Array.from(nvrGroups.entries()).map(([serial, cameras]) => {
                      const filtered = cameras.filter(matchesSearch);
                      if (filtered.length === 0) return null;
                      const isExpanded = expandedNvrs.has(serial);
                      const allInNvrSelected = filtered.every((c) => selectedCameras.has(c.id));
                      const someInNvrSelected = filtered.some((c) => selectedCameras.has(c.id));

                      return (
                        <div key={serial} className="border rounded-lg overflow-hidden">
                          {/* NVR header */}
                          <div className="flex items-center gap-2 p-2.5 bg-muted/30">
                            <button
                              onClick={() => toggleNvr(serial)}
                              className={cn(
                                'w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors',
                                allInNvrSelected
                                  ? 'bg-primary border-primary text-primary-foreground'
                                  : someInNvrSelected
                                    ? 'bg-primary/30 border-primary/60'
                                    : 'border-muted-foreground/30',
                              )}
                            >
                              {allInNvrSelected && <Check className="w-3 h-3" />}
                            </button>
                            <button
                              onClick={() => toggleExpandNvr(serial)}
                              className="flex items-center gap-2 flex-1 min-w-0 text-left"
                            >
                              <Server className="w-4 h-4 text-indigo-500 shrink-0" />
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium">NVR {serial}</div>
                                <div className="text-xs text-muted-foreground">{filtered.length} canal{filtered.length !== 1 ? 'es' : ''}</div>
                              </div>
                              <ChevronDown className={cn('w-4 h-4 text-muted-foreground transition-transform', isExpanded && 'rotate-180')} />
                            </button>
                          </div>

                          {/* NVR channels */}
                          {isExpanded && (
                            <div className="border-t divide-y">
                              {filtered.map((cam) => (
                                <CameraCheckboxRow
                                  key={cam.id}
                                  camera={cam}
                                  isSelected={selectedCameras.has(cam.id)}
                                  onToggle={() => toggleCamera(cam.id)}
                                  indent
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {/* Standalone cameras */}
                    {standaloneCameras.filter(matchesSearch).length > 0 && (
                      <div className="border rounded-lg overflow-hidden">
                        <div className="px-2.5 py-2 bg-muted/30">
                          <div className="flex items-center gap-2 text-sm font-medium">
                            <CameraIcon className="w-4 h-4 text-muted-foreground" />
                            Cámaras independientes
                            <span className="text-xs text-muted-foreground font-normal">
                              ({standaloneCameras.filter(matchesSearch).length})
                            </span>
                          </div>
                        </div>
                        <div className="border-t divide-y">
                          {standaloneCameras.filter(matchesSearch).map((cam) => (
                            <CameraCheckboxRow
                              key={cam.id}
                              camera={cam}
                              isSelected={selectedCameras.has(cam.id)}
                              onToggle={() => toggleCamera(cam.id)}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </>
            )}

            {/* ─── Panels tab ─── */}
            {tab === 'panels' && (
              <>
                {panelsBySite.size === 0 ? (
                  <div className="text-center py-10 text-muted-foreground text-sm">
                    Todos los sitios ya están asignados a este cliente.
                  </div>
                ) : (
                  Array.from(panelsBySite.entries()).map(([siteId, { siteName, panels }]) => {
                    const filteredPanels = panels.filter(matchesPanelSearch);
                    if (filteredPanels.length === 0) return null;
                    const isSiteSelected = selectedSites.has(siteId);

                    return (
                      <button
                        key={siteId}
                        onClick={() => toggleSite(siteId)}
                        className={cn(
                          'w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-colors',
                          isSiteSelected
                            ? 'border-primary/50 bg-primary/5'
                            : 'border-transparent hover:bg-muted/50',
                        )}
                      >
                        <div className={cn(
                          'w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors',
                          isSiteSelected
                            ? 'bg-primary border-primary text-primary-foreground'
                            : 'border-muted-foreground/30',
                        )}>
                          {isSiteSelected && <Check className="w-3 h-3" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{siteName}</div>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                            {filteredPanels.map((p) => (
                              <span key={p.id} className="flex items-center gap-1">
                                <span
                                  className="w-1.5 h-1.5 rounded-full"
                                  style={{ backgroundColor: PANEL_STATUS_COLORS[p.status] ?? '#6B7280' }}
                                />
                                <span className="font-mono">{p.serial_number}</span>
                                <span className="text-muted-foreground/60">({p.model ?? 'Panel'})</span>
                              </span>
                            ))}
                          </div>
                        </div>
                        <Radio className="w-4 h-4 text-muted-foreground/50 shrink-0" />
                      </button>
                    );
                  })
                )}
              </>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between pt-2 border-t">
          <div className="text-xs text-muted-foreground">
            {selectedCameras.size > 0 && (
              <span>{selectedCameras.size} cámara{selectedCameras.size !== 1 ? 's' : ''}</span>
            )}
            {selectedCameras.size > 0 && selectedSites.size > 0 && <span> + </span>}
            {selectedSites.size > 0 && (
              <span>{selectedSites.size} sitio{selectedSites.size !== 1 ? 's' : ''} (alarma)</span>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={totalSelected === 0 || saving}
              className="gap-2"
            >
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Asignar ({totalSelected})
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Reusable camera checkbox row for the assign dialog. */
function CameraCheckboxRow({
  camera, isSelected, onToggle, indent,
}: {
  camera: Camera;
  isSelected: boolean;
  onToggle: () => void;
  indent?: boolean;
}) {
  const statusCfg = CAMERA_STATUS_CONFIG[camera.status];
  return (
    <button
      onClick={onToggle}
      className={cn(
        'w-full flex items-center gap-3 p-2.5 text-left transition-colors',
        indent && 'pl-10',
        isSelected ? 'bg-primary/5' : 'hover:bg-muted/50',
      )}
    >
      <div className={cn(
        'w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors',
        isSelected
          ? 'bg-primary border-primary text-primary-foreground'
          : 'border-muted-foreground/30',
      )}>
        {isSelected && <Check className="w-3 h-3" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{camera.name}</div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {camera.channel_no && <span>Ch {camera.channel_no}</span>}
          {camera.source && (
            <span className={cn(
              'px-1 py-0.5 rounded text-[9px] font-semibold',
              camera.source === 'HPP' ? 'bg-violet-500/20 text-violet-600' : 'bg-sky-500/20 text-sky-600',
            )}>
              {camera.source}
            </span>
          )}
        </div>
      </div>
      <span
        className="w-2 h-2 rounded-full shrink-0"
        style={{ backgroundColor: statusCfg?.color ?? '#6B7280' }}
        title={statusCfg?.label}
      />
    </button>
  );
}

// ─── Client Picker ──────────────────────────────────────────


// ─── Empty State ────────────────────────────────────────────

function EmptyState({ hasFilters, onSync, syncing, accountId, onAssign }: {
  hasFilters: boolean;
  onSync: () => void;
  syncing: boolean;
  accountId?: string | null;
  onAssign?: () => void;
}) {
  return (
    <div className="col-span-full flex flex-col items-center justify-center py-20 text-center">
      <div className="w-20 h-20 rounded-2xl bg-muted flex items-center justify-center mb-4">
        <VideoOff className="w-10 h-10 text-muted-foreground/40" />
      </div>
      {hasFilters ? (
        <>
          <p className="text-foreground font-medium mb-1">Sin resultados</p>
          <p className="text-muted-foreground text-sm max-w-xs">
            No se encontraron cámaras con los filtros actuales. Intenta con otros criterios.
          </p>
        </>
      ) : accountId && onAssign ? (
        <>
          <p className="text-foreground font-medium mb-1">Esta cuenta no tiene cámaras</p>
          <p className="text-muted-foreground text-sm max-w-xs mb-4">
            Asigna cámaras a los sitios de esta cuenta para comenzar a monitorear.
          </p>
          <button
            onClick={onAssign}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="w-4 h-4" />
            Ir a gestión de cuenta
          </button>
        </>
      ) : (
        <>
          <p className="text-foreground font-medium mb-1">Sin cámaras configuradas</p>
          <p className="text-muted-foreground text-sm max-w-xs mb-4">
            Conecta cámaras ONVIF o Hikvision desde el Gateway para comenzar a monitorear.
          </p>
          <button
            onClick={onSync}
            disabled={syncing}
            className={cn(
              'flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors',
              'bg-secondary text-secondary-foreground hover:bg-secondary/80',
              syncing && 'opacity-60 cursor-not-allowed'
            )}
          >
            {syncing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <CloudDownload className="w-4 h-4" />
            )}
            {syncing ? 'Sincronizando...' : 'Sincronizar desde Hikvision'}
          </button>
        </>
      )}
    </div>
  );
}

// ─── Live stream sharing (card → fullscreen) ───────────────
// Stores the card's WebRTC MediaStream so FullscreenView can reuse it instantly.
interface LiveStreamInfo {
  stream: MediaStream;
  mode: 'webrtc' | 'mse';
  bitrateKbps: number | null;
  resolution: string | null;
}
const liveStreamMap = new Map<string, LiveStreamInfo>();

// ─── Camera Card ────────────────────────────────────────────

function CameraCard({
  camera, isSelected, onClick, onFullscreen, compact, onHlsReady,
}: {
  camera: Camera;
  isSelected: boolean;
  onClick: () => void;
  onFullscreen: () => void;
  compact: boolean;
  onHlsReady?: (hls: import('hls.js').default | null) => void;
}) {
  const cardRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [hlsError, setHlsError] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [streamKbps, setStreamKbps] = useState<number | null>(null);
  // go2rtc snapshot state: timestamp drives cache-busting, bool tracks successful load
  const [go2rtcSnapTs, setGo2rtcSnapTs] = useState(() => Date.now());
  const [go2rtcSnapOk, setGo2rtcSnapOk] = useState(false);
  const { url: hlsUrl, error: streamError, retry } = useHlsUrl(camera);
  const nvrSnapshot = useSnapshotUrl(camera, 5000);
  // go2rtc WebRTC player — use sub stream for grid cards (faster, lower res)
  const go2rtcStreamId = camera.go2rtc_stream_id
    ? `${camera.go2rtc_stream_id}_sub`
    : null;
  const go2rtc = useGo2rtcPlayer(videoRef, go2rtcStreamId);
  const isGo2rtc = !!camera.go2rtc_stream_id;

  // Publish live WebRTC stream for fullscreen reuse
  useEffect(() => {
    if (isGo2rtc && go2rtc.state === 'connected' && go2rtc.mode === 'webrtc' && videoRef.current?.srcObject instanceof MediaStream) {
      liveStreamMap.set(camera.id, {
        stream: videoRef.current.srcObject,
        mode: go2rtc.mode,
        bitrateKbps: go2rtc.bitrateKbps,
        resolution: go2rtc.resolution,
      });
    }
    return () => { liveStreamMap.delete(camera.id); };
  }, [camera.id, go2rtc.state, go2rtc.mode, go2rtc.bitrateKbps, go2rtc.resolution, isGo2rtc]);

  useEffect(() => {
    // Skip HLS setup when using go2rtc
    if (isGo2rtc) return;
    setHlsError(false);
    setStreamKbps(null);
    if (!hlsUrl || camera.status !== 'ONLINE') return;

    let hls: import('hls.js').default | null = null;

    (async () => {
      const Hls = (await import('hls.js')).default;
      if (!Hls.isSupported() || !videoRef.current) return;

      hls = new Hls(HLS_CONFIG);
      hls.loadSource(hlsUrl);
      hls.attachMedia(videoRef.current);
      onHlsReady?.(hls);
      hls.on(Hls.Events.ERROR, (_e, data) => {
        if (data.fatal) setHlsError(true);
      });
      hls.on(Hls.Events.FRAG_LOADED, (_e, data) => {
        const { loaded, loading } = data.frag.stats;
        const durationMs = (loading.end ?? 0) - (loading.start ?? 0);
        if (durationMs > 0) setStreamKbps(Math.round((loaded * 8) / durationMs));
      });
    })();

    return () => { onHlsReady?.(null); hls?.destroy(); };
  }, [hlsUrl, camera.status]);

  // Sync muted state with the video element after HLS attaches
  useEffect(() => {
    if (videoRef.current) videoRef.current.muted = isMuted;
  }, [isMuted]);

  const isOnline = camera.status === 'ONLINE';
  const hasError = isGo2rtc ? go2rtc.state === 'failed' : (hlsError || !!streamError);
  const isLoading = isGo2rtc ? go2rtc.state === 'connecting' : (isOnline && !hlsUrl && !streamError);
  const hasContent = isGo2rtc ? go2rtc.state === 'connected' : (!!hlsUrl && !hlsError);
  const retryFn = isGo2rtc ? go2rtc.retry : retry;
  const errorMsg = isGo2rtc ? go2rtc.error : streamError;
  const statusColor = CAMERA_STATUS_CONFIG[camera.status]?.color ?? '#6B7280';

  // go2rtc snapshot URL — cache-busted by go2rtcSnapTs.
  // Refresh every 4 s while WebRTC hasn't connected yet (lighter initial flow).
  const go2rtcSnapUrl = isGo2rtc && isOnline && camera.go2rtc_stream_id
    ? `${GO2RTC_BASE}/go2rtc/api/frame.jpeg?src=${encodeURIComponent(camera.go2rtc_stream_id)}&_t=${go2rtcSnapTs}`
    : null;

  useEffect(() => {
    if (!isGo2rtc || !isOnline || go2rtc.state === 'connected') return;
    const timer = setInterval(() => setGo2rtcSnapTs(Date.now()), 4000);
    return () => clearInterval(timer);
  }, [isGo2rtc, isOnline, go2rtc.state]);

  // Live video failed (go2rtc / gateway restart, too many sessions…): try again by itself every 20 s.
  // Before 2026-10-22 every card stayed on "Cámara no transmite" until the user pressed Reintentar.
  useEffect(() => {
    if (!isGo2rtc || !isOnline || go2rtc.state !== 'failed') return;
    const t = setTimeout(() => go2rtc.retry(), 20_000);
    return () => clearTimeout(t);
  }, [isGo2rtc, isOnline, go2rtc.state, go2rtc.retry]);

  // Fullscreen — always use the overlay FullscreenView (uses main stream)
  const handleFullscreen = useCallback(() => {
    onFullscreen();
  }, [onFullscreen]);

  return (
    <div
      ref={cardRef}
      onClick={onClick}
      className={cn(
        'relative rounded-xl overflow-hidden cursor-pointer border transition-all group aspect-video',
        isSelected
          ? 'border-secondary ring-1 ring-secondary/50'
          : 'border-border hover:border-muted-foreground/30',
      )}
    >
      {/* go2rtc: snapshot as lightweight background while WebRTC connects */}
      {isGo2rtc && isOnline && go2rtcSnapUrl && (
        <img
          src={go2rtcSnapUrl}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          onLoad={() => setGo2rtcSnapOk(true)}
          onError={() => setGo2rtcSnapOk(false)}
        />
      )}
      {/* go2rtc: video element — fades in once WebRTC/MSE stream is active */}
      {isGo2rtc && isOnline && (
        <video
          ref={videoRef}
          autoPlay
          muted={isMuted}
          playsInline
          className={cn(
            'absolute inset-0 w-full h-full object-cover transition-opacity duration-500',
            !hasContent && 'opacity-0',
          )}
        />
      )}

      {/* Non-go2rtc: snapshot as persistent background layer */}
      {!isGo2rtc && isOnline && nvrSnapshot.url && (
        <img
          src={nvrSnapshot.url}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
      )}
      {/* Non-go2rtc: video element always rendered; fades in once stream is active */}
      {!isGo2rtc && isOnline && (
        <video
          ref={videoRef}
          autoPlay
          muted={isMuted}
          playsInline
          className={cn(
            'absolute inset-0 w-full h-full object-cover transition-opacity duration-500',
            !hasContent && 'opacity-0',
          )}
        />
      )}

      {/* Overlay: offline / error / loading without any snapshot */}
      {/* go2rtc with a working snapshot: keep showing the image (the video retries by itself) */}
      {isGo2rtc && isOnline && hasError && go2rtcSnapOk && (
        <button
          onClick={(e) => { e.stopPropagation(); retryFn(); }}
          className="absolute bottom-2 left-2 z-10 flex items-center gap-1 rounded bg-black/60 px-1.5 py-0.5 text-[10px] text-white/80 hover:text-white"
          title="El video en vivo no conectó; se reintenta solo cada 20 s"
        >
          <RefreshCw className="h-3 w-3" /> Solo imagen
        </button>
      )}
      {(isGo2rtc
        ? (!isOnline || (hasError && !go2rtcSnapOk) || (isLoading && !go2rtcSnapOk))
        : (!isOnline || hasError || (isLoading && !nvrSnapshot.url))) && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-muted/80 dark:bg-black/60 gap-2 z-10">
          {isLoading ? (
            <Loader2 className="w-8 h-8 text-muted-foreground/30 animate-spin" />
          ) : hasError && isOnline ? (
            <VideoOff className="w-8 h-8 text-amber-500/60" />
          ) : camera.status === 'ERROR' ? (
            <VideoOff className="w-8 h-8 text-destructive/40" />
          ) : (
            <VideoOff className="w-8 h-8 text-muted-foreground/30" />
          )}
          {!compact && (
            <span className="text-muted-foreground/50 text-xs text-center px-4">
              {isLoading ? 'Conectando...' : hasError && isOnline
                ? (errorMsg === 'STREAM_ENCRYPTED' ? 'Cifrado de video activado' : 'Cámara no transmite')
                : !isOnline ? CAMERA_STATUS_CONFIG[camera.status]?.label : ''}
            </span>
          )}
          {hasError && isOnline && !compact && (
            <button
              onClick={(e) => { e.stopPropagation(); retryFn(); }}
              className="flex items-center gap-1 text-xs text-muted-foreground/60 hover:text-muted-foreground transition-colors"
            >
              <RefreshCw className="w-3 h-3" /> Reintentar
            </button>
          )}
        </div>
      )}



      {/* Top-right actions (visible on hover) */}
      <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        {/* Mute toggle — shown when live video is playing (go2rtc WebRTC or HLS) */}
        {isOnline && ((isGo2rtc && go2rtc.state === 'connected') || (!isGo2rtc && hlsUrl && !hlsError)) && (
          <button
            onClick={(e) => { e.stopPropagation(); setIsMuted((m) => !m); }}
            className="p-1.5 rounded-md bg-black/60 text-white/80 hover:text-white transition-colors"
            title={isMuted ? 'Activar audio' : 'Silenciar'}
          >
            {isMuted
              ? <VolumeX className="w-3.5 h-3.5" />
              : <Volume2 className="w-3.5 h-3.5" />}
          </button>
        )}
        <button
          onClick={(e) => { e.stopPropagation(); handleFullscreen(); }}
          className="p-1.5 rounded-md bg-black/60 text-white/80 hover:text-white transition-colors"
          title="Pantalla completa"
        >
          <Maximize2 className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Status indicator (top-left) */}
      <div className="absolute top-2 left-2">
        <div className="flex items-center gap-1.5 bg-black/60 rounded-md px-2 py-1">
          <span
            className={cn('w-2 h-2 rounded-full', isOnline && 'animate-pulse')}
            style={{ backgroundColor: statusColor }}
          />
          {!compact && (
            <span className="text-white/80 text-[10px] font-medium uppercase tracking-wide">
              {CAMERA_STATUS_CONFIG[camera.status]?.label}
            </span>
          )}
        </div>
      </div>

      {/* Source & Device Type badges (top-left, below status) */}
      {!compact && (
        <div className="absolute top-9 left-2 flex gap-1">
          {/* Source badge: HCT / HPP / GATEWAY */}
          {camera.source && CAMERA_SOURCE_CONFIG[camera.source] && (
            <span
              className="text-white text-[9px] font-semibold px-1.5 py-0.5 rounded"
              style={{ backgroundColor: `${CAMERA_SOURCE_CONFIG[camera.source].color}CC` }}
            >
              {CAMERA_SOURCE_CONFIG[camera.source].label}
            </span>
          )}
          {/* Device type badge: NVR channel */}
          {camera.device_type === 'nvr_channel' && (
            <span className="bg-indigo-600/80 text-white text-[9px] font-semibold px-1.5 py-0.5 rounded">
              NVR
            </span>
          )}
        </div>
      )}

      {/* Bottom overlay */}
      <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 to-transparent p-3 pt-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-white text-sm font-medium truncate">{camera.name}</span>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {/* go2rtc: transport + bitrate in one compact line */}
            {isGo2rtc && go2rtc.state === 'connected' && (
              <span className="text-emerald-400 text-[10px] font-mono tabular-nums flex items-center gap-1">
                {go2rtc.mode === 'webrtc' ? 'WebRTC' : 'MSE'}
                {go2rtc.bitrateKbps != null && (
                  <>
                    <span className="text-white/30">·</span>
                    {go2rtc.bitrateKbps >= 1000
                      ? `${(go2rtc.bitrateKbps / 1000).toFixed(1)}M`
                      : `${go2rtc.bitrateKbps}k`}
                  </>
                )}
                {go2rtc.resolution && (
                  <>
                    <span className="text-white/30">·</span>
                    <span className="text-white/50">{go2rtc.resolution}</span>
                  </>
                )}
              </span>
            )}
            {/* HLS bitrate (non-go2rtc) */}
            {streamKbps !== null && !isGo2rtc && (
              <span className="text-green-400 text-[10px] font-mono tabular-nums">
                {streamKbps >= 1000
                  ? `${(streamKbps / 1000).toFixed(1)} Mbps`
                  : `${streamKbps} kbps`}
              </span>
            )}
            {camera.is_recording && (
              <span className="flex items-center gap-1 text-red-400 text-xs animate-pulse">
                <Circle className="w-2 h-2 fill-current" />
                REC
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Detail Panel ───────────────────────────────────────────

function CameraDetailPanel({
  camera, onClose, onFullscreen,
}: {
  camera: Camera;
  onClose: () => void;
  onFullscreen: () => void;
}) {
  const { fetchCameras, selectCamera } = useCamerasStore();
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [disableReason, setDisableReason] = useState('');
  const [acting, setActing] = useState(false);
  const [togglingDemo, setTogglingDemo] = useState(false);

  const handleToggleDemo = async (next: boolean) => {
    setTogglingDemo(true);
    try {
      await setCameraPublicDemo(camera.id, next);
      await fetchCameras();
    } catch (err) {
      console.error('[CameraDetailPanel] setCameraPublicDemo failed', err);
      alert(err instanceof Error ? err.message : 'No se pudo actualizar la visibilidad demo');
    } finally {
      setTogglingDemo(false);
    }
  };

  const handleDisable = async () => {
    setActing(true);
    try {
      await disableCamera(camera.id, { reason: disableReason.trim() || undefined, deviceName: camera.name });
      setShowDisableModal(false);
      setDisableReason('');
      await fetchCameras();
      selectCamera(null);
    } finally {
      setActing(false);
    }
  };

  const handleEnable = async () => {
    setActing(true);
    try {
      await enableCamera(camera.id, { deviceName: camera.name });
      await fetchCameras();
      selectCamera(null);
    } finally {
      setActing(false);
    }
  };

  return (
    <div className="w-80 border-l border-border bg-card flex flex-col h-screen overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-lg font-semibold text-foreground truncate pr-2">{camera.name}</h3>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors shrink-0"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant={camera.status === 'ONLINE' ? 'default' : 'secondary'} className="text-xs">
            {camera.status === 'ONLINE' ? <Wifi className="w-3 h-3 mr-1" /> : <WifiOff className="w-3 h-3 mr-1" />}
            {CAMERA_STATUS_CONFIG[camera.status]?.label}
          </Badge>
          {camera.is_recording && (
            <Badge variant="destructive" className="text-xs">
              <Circle className="w-2 h-2 fill-current mr-1" />
              REC
            </Badge>
          )}
          {camera.is_disabled && (
            <Badge variant="outline" className="text-xs border-orange-500 text-orange-600">
              <PowerOff className="w-3 h-3 mr-1" />
              Desactivada
            </Badge>
          )}
          {camera.is_public_demo && (
            <Badge className="text-xs bg-amber-500 hover:bg-amber-500 text-white">
              <Rocket className="w-3 h-3 mr-1" />
              Demo pública
            </Badge>
          )}
        </div>
      </div>

      {/* Preview */}
      <div className="p-4 border-b border-border">
        <div className="aspect-video rounded-lg overflow-hidden bg-muted relative">
          {camera.status === 'ONLINE' ? (
            <MiniPlayer camera={camera} />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <VideoOff className="w-8 h-8 text-muted-foreground/30" />
            </div>
          )}
          <button
            onClick={onFullscreen}
            className="absolute bottom-2 right-2 p-1.5 rounded-md bg-black/60 text-white/80 hover:text-white transition-colors"
            title="Pantalla completa"
          >
            <Maximize2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Disable / Enable button - below preview */}
      <div className="px-4 py-3 border-b border-border">
        {camera.is_disabled ? (
          <Button
            variant="outline"
            size="sm"
            className="w-full gap-2 border-green-500 text-green-600 hover:bg-green-50 dark:hover:bg-green-950"
            onClick={handleEnable}
            disabled={acting}
          >
            {acting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Power className="w-4 h-4" />}
            Reactivar cámara
          </Button>
        ) : (
          <Button
            variant="outline"
            size="sm"
            className="w-full gap-2 border-orange-500 text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-950"
            onClick={() => setShowDisableModal(true)}
            disabled={acting}
          >
            <PowerOff className="w-4 h-4" />
            Desactivar cámara
          </Button>
        )}
        {camera.is_disabled && camera.disabled_reason && (
          <p className="text-xs text-muted-foreground mt-2 italic">{camera.disabled_reason}</p>
        )}
      </div>

      {/* Public demo visibility toggle */}
      <div className="px-4 py-3 border-b border-border">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 accent-amber-500"
            checked={camera.is_public_demo}
            disabled={togglingDemo}
            onChange={(e) => handleToggleDemo(e.target.checked)}
          />
          <span className="text-sm">
            <span className="font-medium text-foreground flex items-center gap-1.5">
              <Rocket className="w-3.5 h-3.5 text-amber-500" />
              Visible en demo pública
              {togglingDemo && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />}
            </span>
            <span className="text-xs text-muted-foreground block mt-0.5">
              Cualquier usuario que se una a la demo pública desde la app móvil podrá ver esta cámara.
            </span>
          </span>
        </label>
      </div>

      {/* Info */}
      <div className="flex-1 overflow-auto p-4 space-y-4">
        <DetailRow label="Protocolo" value={CAMERA_PROTOCOL_LABELS[camera.protocol] ?? camera.protocol} />
        <DetailRow label="Host" value={`${camera.host}:${camera.port}`} mono />
        {camera.rtsp_url && (
          <DetailRow
            label="RTSP URL"
            value={camera.rtsp_url.replace(/\/\/([^:@]+:[^@]+)@/, '//***:***@')}
            mono
            small
          />
        )}
        {camera.hls_url && <DetailRow label="HLS URL" value={camera.hls_url} mono small />}
        {camera.go2rtc_stream_id && (
          <DetailRow label="go2rtc Stream" value={camera.go2rtc_stream_id} mono />
        )}
        <DetailRow label="Grabando" value={camera.is_recording ? '⏺ Sí' : 'No'} />
        {camera.last_seen_at && (
          <DetailRow label="Última conexión" value={new Date(camera.last_seen_at).toLocaleString('es-GT')} />
        )}
        {camera.zone_id && <DetailRow label="Zona" value={camera.zone_id} />}

        {/* Source & Device Type */}
        {camera.source && CAMERA_SOURCE_CONFIG[camera.source] && (
          <div>
            <p className="text-muted-foreground text-xs uppercase tracking-wider mb-1">Fuente</p>
            <Badge
              className="text-xs"
              style={{ backgroundColor: CAMERA_SOURCE_CONFIG[camera.source].color }}
            >
              {CAMERA_SOURCE_CONFIG[camera.source].label}
            </Badge>
          </div>
        )}
        {camera.device_type && CAMERA_DEVICE_TYPE_CONFIG[camera.device_type] && (
          <DetailRow
            label="Tipo de Dispositivo"
            value={CAMERA_DEVICE_TYPE_CONFIG[camera.device_type].label}
          />
        )}
        {camera.device_type === 'nvr_channel' && camera.nvr_serial && (
          <>
            <DetailRow label="NVR Serial" value={camera.nvr_serial} mono />
            {camera.channel_no && <DetailRow label="Canal" value={String(camera.channel_no)} />}
          </>
        )}

        {/* Client assignment section */}
        <div className="pt-2 border-t border-border">
          <CameraAssignments cameraId={camera.id} />
        </div>
      </div>

      {/* Disable confirmation modal */}
      <Dialog open={showDisableModal} onOpenChange={setShowDisableModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Desactivar cámara</DialogTitle>
            <DialogDescription>
              La cámara <strong>{camera.name}</strong> será marcada como inactiva. Puedes reactivarla desde esta misma pantalla o desde la sección de Bajas.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <label className="text-sm font-medium text-foreground block mb-1.5">
                Razón (opcional)
              </label>
              <Input
                placeholder="Ej: Equipo dañado, fin de contrato..."
                value={disableReason}
                onChange={(e) => setDisableReason(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleDisable()}
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => { setShowDisableModal(false); setDisableReason(''); }}>
                Cancelar
              </Button>
              <Button
                variant="destructive"
                onClick={handleDisable}
                disabled={acting}
                className="gap-2"
              >
                {acting ? <Loader2 className="w-4 h-4 animate-spin" /> : <PowerOff className="w-4 h-4" />}
                Desactivar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DetailRow({
  label, value, mono, small,
}: {
  label: string;
  value: string;
  mono?: boolean;
  small?: boolean;
}) {
  return (
    <div>
      <p className="text-muted-foreground text-xs uppercase tracking-wider mb-1">{label}</p>
      <p className={cn(
        'text-foreground',
        mono && 'font-mono',
        small ? 'text-xs break-all' : 'text-sm',
      )}>
        {value}
      </p>
    </div>
  );
}

// ─── Camera → Client Assignments ────────────────────────────

function CameraAssignments({ cameraId }: { cameraId: string }) {
  const [assignments, setAssignments] = useState<
    Array<ClientCamera & { client: { id: string; full_name: string; email: string } }>
  >([]);
  const [allClients, setAllClients] = useState<Array<{ id: string; full_name: string; email: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [adding, setAdding] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [assignData, clientData] = await Promise.all([
        getCameraAssignments(cameraId),
        listClients(),
      ]);
      setAssignments(assignData);
      setAllClients(clientData);
    } catch {
      // silently fail for now
    } finally {
      setLoading(false);
    }
  }, [cameraId]);

  useEffect(() => { loadData(); }, [loadData]);

  const assignedClientIds = new Set(assignments.map((a) => a.client_id));
  const unassignedClients = allClients.filter((c) => !assignedClientIds.has(c.id));

  const handleAssign = async (clientId: string) => {
    setAdding(true);
    try {
      await assignCameraToClient(cameraId, clientId);
      await loadData();
      setShowAdd(false);
    } catch {
      // could show toast
    } finally {
      setAdding(false);
    }
  };

  const handleUnassign = async (clientId: string) => {
    try {
      await unassignCameraFromClient(cameraId, clientId);
      await loadData();
    } catch {
      // could show toast
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <p className="text-muted-foreground text-xs uppercase tracking-wider flex items-center gap-1.5">
          <Users className="w-3.5 h-3.5" />
          Clientes asignados
        </p>
        <button
          onClick={() => setShowAdd(!showAdd)}
          className="text-muted-foreground hover:text-foreground transition-colors"
          title="Asignar cliente"
        >
          <UserPlus className="w-4 h-4" />
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-3">
          <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
        </div>
      ) : assignments.length === 0 ? (
        <p className="text-muted-foreground/60 text-xs py-2">
          Sin clientes asignados. Esta cámara no es visible para ningún cliente.
        </p>
      ) : (
        <div className="space-y-1.5">
          {assignments.map((a) => (
            <div
              key={a.id}
              className="flex items-center justify-between px-2 py-1.5 rounded-md bg-muted/50 group"
            >
              <div className="min-w-0">
                <p className="text-sm text-foreground truncate">{a.client.full_name}</p>
                <p className="text-xs text-muted-foreground truncate">{a.client.email}</p>
              </div>
              <button
                onClick={() => handleUnassign(a.client_id)}
                className="text-muted-foreground/40 hover:text-destructive transition-colors opacity-0 group-hover:opacity-100 shrink-0 ml-2"
                title="Desasignar"
              >
                <UserMinus className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add client dropdown */}
      {showAdd && unassignedClients.length > 0 && (
        <div className="mt-2 border border-border rounded-lg overflow-hidden max-h-40 overflow-y-auto">
          {unassignedClients.map((c) => (
            <button
              key={c.id}
              onClick={() => handleAssign(c.id)}
              disabled={adding}
              className="w-full text-left px-3 py-2 text-sm hover:bg-muted/50 transition-colors flex items-center justify-between disabled:opacity-50"
            >
              <div className="min-w-0">
                <p className="text-foreground truncate">{c.full_name}</p>
                <p className="text-xs text-muted-foreground truncate">{c.email}</p>
              </div>
              <UserPlus className="w-3.5 h-3.5 text-muted-foreground shrink-0 ml-2" />
            </button>
          ))}
        </div>
      )}
      {showAdd && unassignedClients.length === 0 && !loading && (
        <p className="text-muted-foreground/60 text-xs mt-2">
          Todos los clientes ya están asignados.
        </p>
      )}
    </div>
  );
}

// ─── Mini Player (detail panel preview) ─────────────────────

function MiniPlayer({ camera }: { camera: Camera }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [go2rtcSnapTs, setGo2rtcSnapTs] = useState(() => Date.now());
  const [go2rtcSnapOk, setGo2rtcSnapOk] = useState(false);
  const { url: hlsUrl, error: streamError, retry } = useHlsUrl(camera);
  const nvrSnapshot = useSnapshotUrl(camera, 5000);

  // go2rtc WebRTC/MSE player — use sub stream for mini player (faster)
  const go2rtcStreamId = camera.go2rtc_stream_id
    ? `${camera.go2rtc_stream_id}_sub`
    : null;
  const go2rtc = useGo2rtcPlayer(videoRef, go2rtcStreamId);
  const isGo2rtc = !!camera.go2rtc_stream_id;

  const useSnapshot = isGo2rtc ? false : (!hlsUrl || !!streamError);

  // go2rtc snapshot: poll frame while WebRTC connects (lighter initial flow)
  const go2rtcSnapUrl = isGo2rtc && camera.status === 'ONLINE' && camera.go2rtc_stream_id
    ? `${GO2RTC_BASE}/go2rtc/api/frame.jpeg?src=${encodeURIComponent(camera.go2rtc_stream_id)}&_t=${go2rtcSnapTs}`
    : null;
  useEffect(() => {
    if (!isGo2rtc || camera.status !== 'ONLINE' || go2rtc.state === 'connected') return;
    const timer = setInterval(() => setGo2rtcSnapTs(Date.now()), 4000);
    return () => clearInterval(timer);
  }, [isGo2rtc, camera.status, go2rtc.state]);

  useEffect(() => {
    // Skip HLS when using go2rtc
    if (isGo2rtc) return;
    if (!hlsUrl) return;

    let hls: import('hls.js').default | null = null;

    (async () => {
      const Hls = (await import('hls.js')).default;
      if (!Hls.isSupported() || !videoRef.current) return;

      hls = new Hls(HLS_CONFIG);
      hls.loadSource(hlsUrl);
      hls.attachMedia(videoRef.current);
    })();

    return () => { hls?.destroy(); };
  }, [hlsUrl]);

  const error = isGo2rtc
    ? (go2rtc.state === 'failed' ? go2rtc.error : null)
    : (useSnapshot ? nvrSnapshot.error : streamError);
  const loading = isGo2rtc
    ? (go2rtc.state === 'connecting' && !go2rtcSnapOk)
    : (useSnapshot ? nvrSnapshot.loading : !hlsUrl);
  const retryFn = isGo2rtc ? go2rtc.retry : (useSnapshot ? nvrSnapshot.retry : retry);

  if (error) {
    return (
      <div className="w-full h-full relative">
        {isGo2rtc && <video ref={videoRef} autoPlay muted playsInline className="hidden" />}
        <div className="w-full h-full flex flex-col items-center justify-center gap-2">
          <VideoOff className="w-6 h-6 text-amber-500/60" />
          <span className="text-muted-foreground/50 text-xs">
            {error === 'STREAM_ENCRYPTED' ? 'Cifrado activado' : 'No transmite'}
          </span>
          <button onClick={retryFn} className="flex items-center gap-1 text-xs text-muted-foreground/60 hover:text-muted-foreground">
            <RefreshCw className="w-3 h-3" /> Reintentar
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="w-full h-full relative">
        {isGo2rtc && <video ref={videoRef} autoPlay muted playsInline className="w-full h-full object-cover" />}
        <div className="absolute inset-0 flex items-center justify-center">
          <Loader2 className="w-6 h-6 text-muted-foreground/30 animate-spin" />
        </div>
      </div>
    );
  }

  // go2rtc: layered snapshot + video (same pattern as CameraCard)
  if (isGo2rtc && camera.status === 'ONLINE') {
    return (
      <div className="w-full h-full relative">
        {go2rtcSnapUrl && (
          <img
            src={go2rtcSnapUrl}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            onLoad={() => setGo2rtcSnapOk(true)}
            onError={() => setGo2rtcSnapOk(false)}
          />
        )}
        <video
          ref={videoRef}
          autoPlay
          muted
          playsInline
          className={cn(
            'absolute inset-0 w-full h-full object-cover transition-opacity duration-500',
            go2rtc.state !== 'connected' && 'opacity-0',
          )}
        />
      </div>
    );
  }

  if (useSnapshot && nvrSnapshot.url) {
    return (
      <img
        src={nvrSnapshot.url}
        alt={camera.name}
        className="w-full h-full object-cover"
      />
    );
  }

  return (
    <video
      ref={videoRef}
      autoPlay
      muted
      playsInline
      className="w-full h-full object-cover"
    />
  );
}

// ─── Fullscreen View ────────────────────────────────────────

function FullscreenView({ camera, onClose }: { camera: Camera; onClose: () => void }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [streamKbps, setStreamKbps] = useState<number | null>(null);
  const [go2rtcSnapTs, setGo2rtcSnapTs] = useState(() => Date.now());
  const [go2rtcSnapOk, setGo2rtcSnapOk] = useState(false);
  const { url: hlsUrl, error: streamError, retry } = useHlsUrl(camera);
  const nvrSnapshot = useSnapshotUrl(camera, 3000); // Faster polling in fullscreen

  // Stream configuration — defaults to mid stream + auto mode
  const [streamQuality, setStreamQuality] = useState<'main' | 'mid' | 'sub'>('mid');
  const [transportMode, setTransportMode] = useState<'auto' | 'webrtc' | 'mse'>('auto');
  const [showConfig, setShowConfig] = useState(false);
  // Audio controls
  const [isMuted, setIsMuted] = useState(true);
  const talkback = useGo2rtcTalkback();

  // Sync muted state imperatively — React's `muted` prop doesn't update after mount
  useEffect(() => {
    if (videoRef.current) videoRef.current.muted = isMuted;
  }, [isMuted]);

  // Reuse the card's live stream for instant playback
  const cardStream = useRef(liveStreamMap.get(camera.id) ?? null);
  const [usingCardStream, setUsingCardStream] = useState(!!cardStream.current);

  // Apply card's stream immediately on mount
  useEffect(() => {
    if (cardStream.current && videoRef.current && usingCardStream) {
      videoRef.current.srcObject = cardStream.current.stream;
      videoRef.current.play().catch(() => {});
    }
  }, [usingCardStream]);

  // Only create a new go2rtc connection when user changes settings
  const go2rtcStreamId = !usingCardStream && camera.go2rtc_stream_id
    ? streamQuality === 'sub'
      ? `${camera.go2rtc_stream_id}_sub`
      : streamQuality === 'mid'
        ? `${camera.go2rtc_stream_id}_mid`
        : camera.go2rtc_stream_id
    : null;

  // go2rtc WebRTC/MSE player — fullscreen with configurable quality + transport
  const go2rtc = useGo2rtcPlayer(videoRef, go2rtcStreamId, { preferredMode: transportMode });
  const isGo2rtc = !!camera.go2rtc_stream_id;

  const useSnapshot = isGo2rtc ? false : (!hlsUrl || !!streamError);

  useEffect(() => {
    if (isGo2rtc) return; // go2rtc handles its own video
    setStreamKbps(null);
    if (!hlsUrl || camera.status !== 'ONLINE') return;

    let hls: import('hls.js').default | null = null;

    (async () => {
      const Hls = (await import('hls.js')).default;
      if (!Hls.isSupported() || !videoRef.current) return;

      hls = new Hls(HLS_CONFIG);
      hls.loadSource(hlsUrl);
      hls.attachMedia(videoRef.current);
      hls.on(Hls.Events.FRAG_LOADED, (_e, data) => {
        const { loaded, loading } = data.frag.stats;
        const durationMs = (loading.end ?? 0) - (loading.start ?? 0);
        if (durationMs > 0) setStreamKbps(Math.round((loaded * 8) / durationMs));
      });
    })();

    return () => { hls?.destroy(); };
  }, [hlsUrl, camera.status]);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const error = isGo2rtc
    ? (usingCardStream ? null : (go2rtc.state === 'failed' ? go2rtc.error : null))
    : (useSnapshot ? nvrSnapshot.error : streamError);
  const loading = isGo2rtc
    ? (!usingCardStream && go2rtc.state === 'connecting' && !go2rtcSnapOk)
    : (useSnapshot ? nvrSnapshot.loading : (!hlsUrl && !streamError));
  const hasContent = isGo2rtc
    ? (usingCardStream || go2rtc.state === 'connected')
    : (useSnapshot ? !!nvrSnapshot.url : !!hlsUrl);
  const retryFn = isGo2rtc ? go2rtc.retry : (useSnapshot ? nvrSnapshot.retry : retry);

  // go2rtc snapshot for fullscreen — lightest initial view while stream connects
  const go2rtcSnapUrl = isGo2rtc && !usingCardStream && camera.status === 'ONLINE' && camera.go2rtc_stream_id
    ? `/go2rtc-api/frame.jpeg?src=${encodeURIComponent(camera.go2rtc_stream_id)}&_t=${go2rtcSnapTs}`
    : null;
  useEffect(() => {
    if (!isGo2rtc || usingCardStream || camera.status !== 'ONLINE' || go2rtc.state === 'connected') return;
    const timer = setInterval(() => setGo2rtcSnapTs(Date.now()), 3000);
    return () => clearInterval(timer);
  }, [isGo2rtc, usingCardStream, camera.status, go2rtc.state]);

  // Helper to trigger new go2rtc connection when user changes settings
  const switchStream = useCallback((quality: 'main' | 'mid' | 'sub', transport: 'auto' | 'webrtc' | 'mse') => {
    setStreamQuality(quality);
    setTransportMode(transport);
    setUsingCardStream(false);
  }, []);

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-3 bg-black/80">
        <div className="flex items-center gap-3">
          <CameraIcon className="w-5 h-5 text-white/60" />
          <span className="text-white font-medium">{camera.name}</span>
          <Badge variant={camera.status === 'ONLINE' ? 'default' : 'secondary'} className="text-xs">
            {CAMERA_STATUS_CONFIG[camera.status]?.label}
          </Badge>
          {/* go2rtc stats — from card stream or own connection */}
          {isGo2rtc && (usingCardStream || go2rtc.state === 'connected') && (() => {
            const info = usingCardStream ? cardStream.current : null;
            const modeLabel = usingCardStream ? (info?.mode === 'webrtc' ? 'WebRTC' : 'MSE') : (go2rtc.mode === 'webrtc' ? 'WebRTC' : 'MSE');
            const kbps = usingCardStream ? info?.bitrateKbps : go2rtc.bitrateKbps;
            const res = usingCardStream ? info?.resolution : go2rtc.resolution;
            return (
              <span className="text-emerald-400 text-xs font-mono tabular-nums flex items-center gap-1.5">
                {modeLabel}
                {usingCardStream && <><span className="text-white/30">·</span><span className="text-white/50">Sub</span></>}
                {kbps != null && (
                  <>
                    <span className="text-white/30">·</span>
                    {kbps >= 1000 ? `${(kbps / 1000).toFixed(1)} Mbps` : `${kbps} kbps`}
                  </>
                )}
                {res && (
                  <>
                    <span className="text-white/30">·</span>
                    <span className="text-white/60">{res}</span>
                  </>
                )}
              </span>
            );
          })()}
          {!isGo2rtc && useSnapshot && (
            <span className="bg-blue-600/80 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded">
              Snapshot
            </span>
          )}
          {streamKbps !== null && !useSnapshot && !isGo2rtc && (
            <span className="text-green-400 text-xs font-mono tabular-nums">
              {streamKbps >= 1000
                ? `${(streamKbps / 1000).toFixed(1)} Mbps`
                : `${streamKbps} kbps`}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {/* Mute toggle — go2rtc streams carry audio via WebRTC */}
          {isGo2rtc && (
            <button
              onClick={() => setIsMuted((m) => !m)}
              className="p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/10 transition-colors"
              title={isMuted ? 'Activar audio' : 'Silenciar'}
            >
              {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
            </button>
          )}
          {/* Talk-back — send mic audio to camera (cameras with speaker + two-way audio) */}
          {isGo2rtc && camera.source === 'GATEWAY' && camera.status === 'ONLINE' && camera.supports_two_way_audio && (
            <button
              onClick={() => talkback.toggle(camera.go2rtc_stream_id!)}
              disabled={talkback.isStarting}
              className={cn(
                'p-2 rounded-lg transition-colors',
                talkback.isActive
                  ? 'text-white bg-rose-600 hover:bg-rose-700'
                  : 'text-white/60 hover:text-white hover:bg-white/10',
                talkback.isStarting && 'opacity-50 cursor-wait',
              )}
              title={talkback.isActive ? 'Detener audio' : 'Hablar con cámara'}
            >
              {talkback.isStarting
                ? <Loader2 className="w-5 h-5 animate-spin" />
                : talkback.isActive
                  ? <Mic className="w-5 h-5" />
                  : <MicOff className="w-5 h-5" />}
            </button>
          )}
          {/* Stream config toggle */}
          {isGo2rtc && (
            <button
              onClick={() => setShowConfig((v) => !v)}
              className={cn(
                'p-2 rounded-lg transition-colors',
                showConfig
                  ? 'text-white bg-white/20'
                  : 'text-white/60 hover:text-white hover:bg-white/10',
              )}
              title="Configuración de stream"
            >
              <Settings className="w-5 h-5" />
            </button>
          )}
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/10 transition-colors"
            title="Cerrar (Esc)"
          >
            <Minimize2 className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Stream configuration panel */}
      {isGo2rtc && showConfig && (
        <div className="flex items-center gap-6 px-4 py-2 bg-black/60 border-t border-white/10">
          {/* Stream quality */}
          <div className="flex items-center gap-2">
            <MonitorPlay className="w-4 h-4 text-white/50" />
            <span className="text-white/50 text-xs font-medium">Calidad:</span>
            <div className="flex rounded-md overflow-hidden border border-white/20">
              {(['sub', 'mid', 'main'] as const).map((q) => (
                <button
                  key={q}
                  onClick={() => switchStream(q, transportMode)}
                  className={cn(
                    'px-3 py-1 text-xs font-medium transition-colors',
                    q !== 'sub' && 'border-l border-white/20',
                    streamQuality === q
                      ? 'bg-emerald-600 text-white'
                      : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white',
                  )}
                >
                  {q === 'sub' ? 'Sub' : q === 'mid' ? 'Mid (720p)' : 'Main'}
                </button>
              ))}
            </div>
          </div>

          {/* Transport mode */}
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-white/50" />
            <span className="text-white/50 text-xs font-medium">Transporte:</span>
            <div className="flex rounded-md overflow-hidden border border-white/20">
              {(['auto', 'webrtc', 'mse'] as const).map((m) => (
                <button
                  key={m}
                  onClick={() => switchStream(streamQuality, m)}
                  className={cn(
                    'px-3 py-1 text-xs font-medium transition-colors',
                    m !== 'auto' && 'border-l border-white/20',
                    transportMode === m
                      ? 'bg-emerald-600 text-white'
                      : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white',
                  )}
                >
                  {m === 'auto' ? 'Auto' : m === 'webrtc' ? 'WebRTC' : 'MSE'}
                </button>
              ))}
            </div>
          </div>

          {/* Current status */}
          <div className="flex items-center gap-2 ml-auto">
            {(usingCardStream || go2rtc.state === 'connected') && (
              <span className="text-emerald-400 text-xs font-mono">
                {usingCardStream ? 'WEBRTC' : go2rtc.mode?.toUpperCase()} &bull; {usingCardStream ? 'Sub (tarjeta)' : streamQuality === 'main' ? 'Principal' : streamQuality === 'mid' ? 'Medio (720p)' : 'Secundario'}
                {!usingCardStream && go2rtc.bitrateKbps != null && ` · ${go2rtc.bitrateKbps >= 1000 ? `${(go2rtc.bitrateKbps / 1000).toFixed(1)} Mbps` : `${go2rtc.bitrateKbps} kbps`}`}
                {!usingCardStream && go2rtc.resolution && ` · ${go2rtc.resolution}`}
              </span>
            )}
            {go2rtc.state === 'connecting' && (
              <span className="text-amber-400 text-xs flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" /> Reconectando...
              </span>
            )}
          </div>
        </div>
      )}

      {/* Video / Snapshot */}
      <div className="flex-1 flex items-center justify-center relative">
        {/* go2rtc: snapshot as background while WebRTC/MSE connects (lighter initial flow) */}
        {isGo2rtc && camera.status === 'ONLINE' && go2rtcSnapUrl && (
          <img
            src={go2rtcSnapUrl}
            alt=""
            className="absolute inset-0 w-full h-full object-contain"
            onLoad={() => setGo2rtcSnapOk(true)}
            onError={() => setGo2rtcSnapOk(false)}
          />
        )}
        {/* go2rtc: always render video so hook has the ref — fades in once connected */}
        {isGo2rtc && camera.status === 'ONLINE' && (
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            controls={hasContent && !error}
            className={cn(
              'max-w-full max-h-full transition-opacity duration-500',
              !hasContent && 'opacity-0 absolute inset-0 w-full h-full',
            )}
          />
        )}

        {/* Non-go2rtc video/snapshot */}
        {!isGo2rtc && camera.status === 'ONLINE' && hasContent && !error ? (
          useSnapshot ? (
            <img
              src={nvrSnapshot.url!}
              alt={camera.name}
              className="max-w-full max-h-full object-contain"
            />
          ) : (
            <video
              ref={videoRef}
              autoPlay
              muted
              playsInline
              controls
              className="max-w-full max-h-full"
            />
          )
        ) : null}

        {/* Error overlay */}
        {camera.status === 'ONLINE' && error ? (
          <div className="flex flex-col items-center gap-4 text-white/50 z-10">
            <VideoOff className="w-16 h-16 text-amber-500/60" />
            <p className="text-lg">
              {error === 'STREAM_ENCRYPTED' ? 'Cifrado de video activado' : 'Cámara no transmite'}
            </p>
            <p className="text-sm text-white/30">
              {error === 'STREAM_ENCRYPTED'
                ? 'Desactiva el cifrado de video en la app Hik-Connect para esta cámara'
                : 'Verificar conexión del dispositivo en Hik-Connect'}
            </p>
            <button
              onClick={retryFn}
              className="mt-2 flex items-center gap-2 px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white/70 hover:text-white transition-colors text-sm"
            >
              <RefreshCw className="w-4 h-4" /> Reintentar
            </button>
          </div>
        ) : camera.status === 'ONLINE' && loading ? (
          <div className="flex flex-col items-center gap-4 text-white/50 z-10">
            <Loader2 className="w-12 h-12 animate-spin" />
            <p className="text-lg">Conectando...</p>
          </div>
        ) : camera.status !== 'ONLINE' ? (
          <div className="flex flex-col items-center gap-4 text-white/30">
            <VideoOff className="w-16 h-16" />
            <p className="text-lg">{CAMERA_STATUS_CONFIG[camera.status]?.label ?? 'Sin stream'}</p>
          </div>
        ) : null}
      </div>
    </div>
  );
}
